import { useEffect, useState } from "react";
import { Pressable } from "react-native";
import styled from "styled-components/native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/lib/firebase";
import { colors, font } from "@/lib/theme";
import VerifyingAnimation from "@/components/VerifyingAnimation";

/* ── Styled Components ── */

const Container = styled.View`
  flex: 1;
  background-color: ${colors.white};
`;

const MainContent = styled.View`
  flex: 1;
`;

const BottomSection = styled.View`
  padding: 24px 16px;
  align-items: center;
`;

const ActionButton = styled(Pressable)`
  padding: 12px 16px;
  margin-bottom: 4px;
`;

const ActionButtonText = styled.Text`
  font-size: 16px;
  color: ${colors.brand};
  font-weight: ${font.medium};
`;

const ActionButtonTextMuted = styled.Text`
  font-size: 16px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
`;

const EscapeLink = styled(Pressable)`
  margin-top: 16px;
  padding: 8px;
`;

const EscapeLinkText = styled.Text`
  font-size: 14px;
  color: ${colors.brand};
`;

/* ── Component ── */

type ScreenState = "verifying" | "success" | "error";

// Prevent re-processing on hot reload
const processedTokens = new Set<string>();

export default function AuthTokenScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const router = useRouter();
  const [screenState, setScreenState] = useState<ScreenState>("verifying");
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;

    if (processedTokens.has(token)) {
      setScreenState("error");
      setError("This link has already been used.");
      return;
    }

    processedTokens.add(token);
    verifyToken(token);
  }, [token]);

  async function verifyToken(t: string) {
    setScreenState("verifying");
    setError(null);

    try {
      // Backend handles everything: verify token, create user, accept invite, update clubIds
      const verifyMagicLink = httpsCallable(functions, "verifyMagicLink");
      const result = await callWithRetry(verifyMagicLink, { token: t });
      const data = result.data as any;

      if (!data.success || !data.customToken) {
        throw new Error("Verification failed.");
      }

      setEmail(data.email);

      // Everything is set up by the backend — just sign in
      await signInWithCustomToken(auth, data.customToken);

      // Stay on this screen — NavigationGate will redirect once ClubContext is ready
    } catch (err: any) {
      const message = friendlyErrorMessage(err);
      setError(message);
      setScreenState("error");
    }
  }

  async function handleResend() {
    if (!email) return;
    setResending(true);
    try {
      const sendMagicLink = httpsCallable(functions, "sendMagicLink");
      await sendMagicLink({ email, type: "signin" });
      setError("A new sign-in link has been sent to your email.");
    } catch (err: any) {
      setError("Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  }

  const showButtons = screenState === "error";

  return (
    <Container>
      <MainContent>
        <VerifyingAnimation
          title="Verifying your link..."
          state={screenState}
          errorMessage={error || undefined}
        />
      </MainContent>
      <BottomSection style={{ opacity: showButtons ? 1 : 0 }} pointerEvents={showButtons ? "auto" : "none"}>
        {email && (
          <ActionButton onPress={handleResend} disabled={resending}>
            {resending ? (
              <ActionButtonTextMuted>Sending...</ActionButtonTextMuted>
            ) : (
              <ActionButtonText>Send new sign-in link</ActionButtonText>
            )}
          </ActionButton>
        )}
        <EscapeLink onPress={() => router.replace("/(auth)/login")}>
          <EscapeLinkText>Go to sign in</EscapeLinkText>
        </EscapeLink>
      </BottomSection>
    </Container>
  );
}

/* ── Helpers ── */

function friendlyErrorMessage(error: any): string {
  const code = error?.code || "";
  const msg = error?.message || "";

  if (code === "functions/not-found" || code === "functions/deadline-exceeded") {
    return msg;
  }
  if (code === "functions/resource-exhausted") {
    return msg;
  }
  if (code === "functions/unavailable" || msg.toLowerCase() === "unavailable") {
    return "Service temporarily unavailable. Please try again in a moment.";
  }
  if (code === "functions/internal" || msg.toLowerCase() === "internal") {
    return "Something went wrong. Please try again.";
  }

  // If message looks like a raw error code, use fallback
  if (!msg || msg === msg.toUpperCase() || msg.length < 10) {
    return "Something went wrong. Please try again.";
  }
  return msg;
}

async function callWithRetry(callable: any, data: any, maxRetries = 2, delayMs = 1500) {
  let lastError: any;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await callable(data);
    } catch (error: any) {
      lastError = error;
      const code = error?.code || "";
      const msg = error?.message?.toLowerCase() || "";
      const isTransient =
        (code === "functions/internal" && (!msg || msg === "internal")) ||
        code === "functions/unavailable";
      if (!isTransient || attempt === maxRetries) throw error;
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  throw lastError;
}

